import { normalizeMessageVariables } from "@/lib/campaignHelpers";

/**
 * Shape-safe builder for `sequences` inserts.
 *
 * PostgREST fills keys that are missing from *some* objects of a multi-row
 * insert with NULL instead of the column default. Editor-created steps (new
 * follow-ups, the auto-generated "end" step) omit `fallback_enabled`, while
 * steps loaded from the database carry it — that mix made the whole insert
 * fail with a not-null violation on `sequences.fallback_enabled`.
 *
 * Every row produced here has an identical key set with coerced values, so the
 * insert can never drift again.
 */
export interface SequenceRowInput {
  step_number: number;
  action_type: string;
  delay_hours?: number | null;
  message_template?: string | null;
  include_message?: boolean | null;
  use_inmail?: boolean | null;
  attachment_urls?: any;
  condition_type?: string | null;
  condition_value?: any;
  branch_type?: string | null;
  final_wait_hours?: number | null;
  parent_step_id?: string | null;
  connection_timeout_days?: number | null;
  fallback_template?: string | null;
  fallback_enabled?: boolean | null;
  [key: string]: any;
}

export interface BuildSequenceRowsOptions {
  /** Normalize `{Variable Name}` tokens in message/fallback bodies. */
  normalizeVariables?: boolean;
  /** Drop any fallback content (workspaces without profile pre-fetch). */
  allowFallback?: boolean;
}

export function buildSequenceRows(
  steps: SequenceRowInput[],
  campaignId: string,
  options: BuildSequenceRowsOptions = {}
) {
  const { normalizeVariables = true, allowFallback = true } = options;

  const norm = (value?: string | null) => {
    if (!value) return null;
    return normalizeVariables ? normalizeMessageVariables(value) : value;
  };

  return (steps || []).map((seq) => {
    const fallbackEnabled = allowFallback ? seq.fallback_enabled === true : false;
    const fallbackTemplate = allowFallback ? norm(seq.fallback_template) : null;

    return {
      campaign_id: campaignId,
      step_number: seq.step_number,
      action_type: seq.action_type,
      delay_hours: seq.delay_hours ?? 0,
      message_template: norm(seq.message_template),
      include_message: seq.include_message === true,
      use_inmail: seq.use_inmail === true,
      attachment_urls: seq.attachment_urls ?? null,
      condition_type: seq.condition_type ?? null,
      condition_value: seq.condition_value ?? null,
      branch_type: seq.branch_type ?? null,
      final_wait_hours: seq.final_wait_hours ?? 0,
      parent_step_id: seq.parent_step_id ?? null,
      fallback_template: fallbackTemplate,
      fallback_enabled: fallbackEnabled,
    };
  });
}

/**
 * Atomically replaces a campaign's sequence.
 *
 * Saving used to be `delete()` followed by `insert()`. When the insert failed
 * (e.g. the `fallback_enabled` not-null regression) the delete had already
 * committed, leaving the campaign with **no** sequence at all — the Connection
 * Request step silently disappeared for the customer. The database function
 * does both inside one transaction and rejects an empty sequence outright.
 */
export async function saveCampaignSequences(
  client: { rpc: (fn: string, args: Record<string, unknown>) => any },
  campaignId: string,
  steps: SequenceRowInput[],
  options: BuildSequenceRowsOptions = {}
) {
  const rows = buildSequenceRows(steps, campaignId, options);
  if (rows.length === 0) {
    throw new Error("A campaign must keep at least one sequence step — nothing was saved.");
  }
  const { error } = await client.rpc("replace_campaign_sequences", {
    p_campaign_id: campaignId,
    p_rows: rows,
  });
  if (error) throw error;
  return rows.length;
}
